/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import {
  FlexContainer,
  Icon,
  Label,
  useDeviceSize,
  StatusType,
  IconName,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useRecoilValue } from 'recoil';
import {
  Asset,
  DOCUMENT_FACIAL,
  DOCUMENT_SELFIE,
  FacialAuthenticationStatus,
  infoFacial,
  StatusTypeList,
  GovernmentServices,
  external,
  StatusOperations,
  tInfo,
  FacialAuthenticationPayload,
} from '../../states';
import { antispoofingState, livenessValidationState } from '../../states/atoms';
import { EmptyFilesUpload, EmptyFilesError } from '../icons';
import { ImageLoad } from './ImageLoad';
import { StatusTypeProps } from './StatusIcon';
import {
  FacialContainer,
  ResultsInfoBar,
  SelfieValidationResult,
  FacialCheckGrid,
  BiometricResultsContainer,
} from './Styles';

type Props = {
  facialState?: FacialAuthenticationStatus;
  livenessState?: StatusTypeProps;
  assets?: Asset[];
  loading?: boolean;
  isDetail?: boolean;
  governmentService?: GovernmentServices;
  provider?: string;
  operationStatus?: StatusType | StatusOperations | StatusTypeList;
  antispoofValidation?: any;
  idLabelSelphi?: string;
  idLabelPhotoDocument?: string;
  idMatchingResult?: string;
  idMatchingSelphi?: string;
  idMatchingPhotoDocument?: string;
  idResultMatchingIcon?: string;
  facialAuthenticationData?: FacialAuthenticationPayload;
  governmentFacialCapture?: Asset;
};

export const SelfieValidations = ({
  facialState,
  livenessState,
  assets = [],
  loading,
  isDetail = false,
  provider,
  operationStatus,
  antispoofValidation,
  idLabelSelphi,
  idLabelPhotoDocument,
  idMatchingResult,
  idMatchingSelphi,
  idMatchingPhotoDocument,
  idResultMatchingIcon,
  governmentFacialCapture,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useDeviceSize();
  const livenessValidation = useRecoilValue(livenessValidationState);
  const antispoofingActivated = useRecoilValue(antispoofingState);

  const operationError =
    operationStatus === StatusOperations.CONNECTION_ERROR ||
    operationStatus === StatusOperations.CANCELLED ||
    operationStatus === StatusOperations.ERROR;

  const isExternalProvider = Object.values(GovernmentServices).includes(
    provider as GovernmentServices
  );
  const showLiveness = livenessValidation && !isDetail;
  const showAntispoof =
    antispoofingActivated && antispoofValidation && !loading && !isDetail;
  const isImpossibleToCheck =
    facialState?.toUpperCase() ===
      FacialAuthenticationStatus.invalidExtractions.toUpperCase() ||
    facialState?.toUpperCase() ===
      FacialAuthenticationStatus.none.toUpperCase() ||
    facialState?.toUpperCase() ===
      FacialAuthenticationStatus.pose.toUpperCase() ||
    facialState?.toUpperCase() ===
      FacialAuthenticationStatus.uncertain.toUpperCase();

  const documentImage = useMemo(
    () =>
      assets.length
        ? isExternalProvider
          ? governmentFacialCapture
          : assets.find(({ type }) => type === DOCUMENT_FACIAL)
        : undefined,
    [assets, isExternalProvider]
  );

  const selfieImage = useMemo(
    () =>
      assets.length
        ? assets.find(({ type }) => type === DOCUMENT_SELFIE)
        : undefined,
    [assets]
  );

  const info: tInfo | undefined = useMemo(() => {
    const type = facialState
      ? isImpossibleToCheck
        ? 'impossibleCheck'
        : facialState.toUpperCase() ===
          FacialAuthenticationStatus.positive.toUpperCase()
        ? `match${isExternalProvider ? `_${external}` : ''}`
        : `notMatch${isExternalProvider ? `_${external}` : ''}`
      : operationStatus === StatusTypeList.EXPIRED
      ? 'expired'
      : operationStatus === StatusTypeList.CANCELLED
      ? 'cancelled'
      : !loading
      ? 'failure'
      : undefined;

    return type ? infoFacial[type] : undefined;
  }, [facialState, operationStatus, loading, isExternalProvider]);

  const governementServiceIcon: IconName | undefined = useMemo(() => {
    switch (provider) {
      case GovernmentServices.renaper:
        return 'RenaperIcon';
      case GovernmentServices.reniec:
        return 'ReniecIcon';
      case GovernmentServices.serpro:
        return 'SerproIcon';
      default:
        return undefined;
    }
  }, [provider]);

  const antispoofState = useMemo(
    () =>
      antispoofValidation?.attack
        ? StatusTypeList.DENIED
        : StatusTypeList.VALIDATE,
    [antispoofValidation]
  );

  const ImageSkeleton = () => (
    <div>
      <Skeleton
        width={isMobile ? 90 : 120}
        height={isMobile ? 90 : 120}
        borderRadius={8}
      />
      <div style={{ marginTop: '0.8rem' }}>
        <Skeleton width="50%" height={18} borderRadius={8} />
      </div>
    </div>
  );

  const getLoadingImages = () => (
    <FlexContainer columnGap="2" alignItems="center">
      <ImageSkeleton />
      <Skeleton width={60} height={60} circle />
      <ImageSkeleton />
    </FlexContainer>
  );

  const getImages = () => (
    <>
      <FacialContainer id={idMatchingSelphi} testId={idMatchingSelphi}>
        {selfieImage ? <ImageLoad asset={selfieImage} /> : <EmptyFilesError />}
      </FacialContainer>

      {info && (
        <Icon
          testId={idResultMatchingIcon}
          key={`provider-${provider}`}
          iconName={info.iconName}
          size="48"
          weight={info.iconWeight}
          color={`${info.iconColor}400`}
        />
      )}

      <FacialContainer
        id={idMatchingPhotoDocument}
        testId={idMatchingPhotoDocument}
      >
        {documentImage ? (
          <ImageLoad asset={documentImage} />
        ) : !isExternalProvider || operationError ? (
          <EmptyFilesError />
        ) : (
          <EmptyFilesUpload />
        )}
      </FacialContainer>
    </>
  );

  return (
    <>
      {loading
        ? getLoadingImages()
        : info && (
            <FacialCheckGrid
              alignItems="center"
              columnGap={isMobile ? '0' : '1.4'}
              justifyContent="space-between"
              gridTemplateColumns={
                isMobile ? '12rem auto 12rem' : '15rem auto 15rem'
              }
              rowGap="0.6"
            >
              {getImages()}
              <>
                <Label
                  className="asset-text selfie-text"
                  fontSize="12"
                  textAlign={isMobile ? 'left' : 'center'}
                  testId={idLabelSelphi}
                  id={idLabelSelphi}
                >
                  {t('SELFIE')}
                </Label>
                {(documentImage || operationError) && (
                  <Label
                    className="asset-text document-text"
                    fontSize="12"
                    textAlign={isMobile ? 'left' : 'center'}
                    testId={idLabelPhotoDocument}
                    id={idLabelPhotoDocument}
                  >
                    {t('DOCUMENT_FACIAL_CAPTURE')}
                  </Label>
                )}
              </>
            </FacialCheckGrid>
          )}

      <BiometricResultsContainer flexDirection="column" rowGap="1.6">
        {(loading || info) && (
          <>
            {/* MATCHING RESULT */}
            <SelfieValidationResult
              columnGap="1.2"
              alignItems="center"
              $generalColor={loading ? 'grey' : info?.iconColor}
              data-test={`selfie-result-${facialState}`}
              key={`provider-${provider}`}
            >
              {governementServiceIcon && (
                <Icon iconName={governementServiceIcon} size="32" />
              )}
              <Label
                fontSize="14"
                textAlign={governementServiceIcon ? 'left' : 'center'}
                testId={idMatchingResult}
                id={idMatchingResult}
                semibold
              >
                {t(
                  loading
                    ? "We're checking faces. Please wait a moment"
                    : info?.message || '',
                  { governmentService: provider || '' }
                )}
              </Label>
            </SelfieValidationResult>

            {/* LIVENESS RESULT */}
            {showLiveness && (
              <ResultsInfoBar
                fontSize="14"
                textAlign="center"
                semibold
                $negative={livenessState === StatusTypeList.STARTED}
                type={livenessState}
                testId="result_liveness_section_text"
                id="result_liveness_section_text"
              >
                {t(`SELFIE_VALIDATION_${livenessState}`)}
              </ResultsInfoBar>
            )}

            {/* ANTISPOOF RESULT */}
            {showAntispoof && (
              <ResultsInfoBar
                fontSize="14"
                textAlign="center"
                semibold
                type={antispoofState}
                testId="result_spoofing_section_text"
                id="result_spoofing_section_text"
              >
                {t(`ANTISPOOF_${antispoofState}`)}
              </ResultsInfoBar>
            )}
          </>
        )}
      </BiometricResultsContainer>
    </>
  );
};
